import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { SquaresFour, type Icon } from '@phosphor-icons/react';
import { MOTION, bewegungReduziert } from '../../../lib/immobilien/motion';
import { ueberlagerungOffen } from './ueberlagerung';

/**
 * Morph-Panel (README §5.3): Ein Widget wächst zum großen Panel über die volle
 * Rasterbreite. Umgesetzt mit framer-motion layoutId – Widget und Panel teilen
 * dieselbe layoutId, framer misst Start (Widget) und Ziel (Panel) selbst.
 *
 * <MorphBuehne offen={!!panel} panel={<MorphPanel …/>}>
 *   …Widget-Raster…
 * </MorphBuehne>
 */

/** Panel-Höhe: max(min(Rasterhöhe, 760), 560). */
export function panelHoehe(rasterHoehe: number): number {
  return Math.max(Math.min(rasterHoehe, 760), 560);
}

function scrollElternteil(el: HTMLElement): HTMLElement | null {
  let p = el.parentElement;
  while (p) {
    const ov = getComputedStyle(p).overflowY;
    if ((ov === 'auto' || ov === 'scroll') && p.scrollHeight > p.clientHeight) return p;
    p = p.parentElement;
  }
  return null;
}

export interface MorphBuehneProps {
  offen: boolean;
  /** Das offene MorphPanel (oder null). */
  panel: React.ReactNode;
  /** Das Widget-Raster. */
  children: React.ReactNode;
}

export function MorphBuehne({ offen, panel, children }: MorphBuehneProps) {
  const buehne = useRef<HTMLDivElement>(null);
  const raster = useRef<HTMLDivElement>(null);
  const [rasterH, setRasterH] = useState(0);
  const fokusVorher = useRef<HTMLElement | null>(null);

  // Rasterhöhe messen (bestimmt die Panel-Höhe)
  useLayoutEffect(() => {
    const el = raster.current;
    if (!el) return;
    const messe = () => setRasterH(el.offsetHeight);
    messe();
    const ro = new ResizeObserver(messe);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Beim Öffnen weich zum Rasteranfang scrollen (wenn > 40 px entfernt);
  // Fokus merken und nach dem Schließen zurückgeben.
  useEffect(() => {
    const el = buehne.current;
    if (!el) return;
    if (offen) {
      fokusVorher.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const eltern = scrollElternteil(el);
      const obenEltern = eltern ? eltern.getBoundingClientRect().top : 0;
      const abstand = el.getBoundingClientRect().top - obenEltern;
      if (Math.abs(abstand) > 40) el.scrollIntoView({ behavior: bewegungReduziert() ? 'auto' : 'smooth', block: 'start' });
    } else if (fokusVorher.current) {
      fokusVorher.current.focus({ preventScroll: true });
      fokusVorher.current = null;
    }
  }, [offen]);

  const h = panelHoehe(rasterH);
  return (
    <div ref={buehne} className="relative" style={{ minHeight: offen ? h : undefined, scrollMarginTop: 64 }}>
      <div
        ref={raster}
        aria-hidden={offen || undefined}
        inert={offen}
        style={{
          opacity: offen ? MOTION.rasterAus.opacity : 1,
          transform: offen ? `scale(${MOTION.rasterAus.scale})` : 'none',
          filter: offen ? `blur(${MOTION.rasterAus.blurPx}px)` : 'none',
          pointerEvents: offen ? 'none' : 'auto',
          transition: 'opacity .5s ease, transform .5s cubic-bezier(.32,.72,0,1), filter .5s ease',
          transformOrigin: 'top center',
        }}
      >
        {children}
      </div>
      {offen && (
        <div className="absolute left-0 right-0 top-0" style={{ height: h, zIndex: 5 }}>
          {panel}
        </div>
      )}
    </div>
  );
}

export interface MorphPanelProps {
  layoutId: string;
  titel: string;
  icon: Icon;
  onSchliessen: () => void;
  children: React.ReactNode;
}

export function MorphPanel({ layoutId, titel, icon: IconKomp, onSchliessen, children }: MorphPanelProps) {
  const schliessenKnopf = useRef<HTMLButtonElement>(null);
  const titelId = `morph-titel-${layoutId}`;

  useEffect(() => {
    schliessenKnopf.current?.focus({ preventScroll: true });
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !ueberlagerungOffen()) onSchliessen(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onSchliessen]);

  const { opacityDauer, transformDauer, verzoegerung } = MOTION.morphInhalt;
  return (
    <motion.section
      layoutId={layoutId}
      transition={{ layout: MOTION.morph }}
      role="region"
      aria-labelledby={titelId}
      className="immo-glas-stark flex flex-col overflow-hidden h-full"
      style={{ borderRadius: 30 }}
    >
      <motion.div
        className="flex flex-col flex-1 min-h-0"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          opacity: { duration: opacityDauer, ease: 'easeOut', delay: verzoegerung },
          y: { duration: transformDauer, ease: MOTION.morph.ease, delay: verzoegerung },
        }}
      >
        <div className="flex items-center justify-between" style={{ gap: 12, padding: '18px 20px 12px' }}>
          <h2 id={titelId} className="flex items-center whitespace-nowrap m-0 min-w-0" style={{ gap: 10, fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' }}>
            <IconKomp weight="fill" size={22} color="var(--acc)" />
            <span className="truncate">{titel}</span>
          </h2>
          <button
            ref={schliessenKnopf}
            type="button"
            onClick={onSchliessen}
            className="immo-hover-flaeche flex items-center cursor-pointer shrink-0"
            style={{ height: 32, padding: '0 13px 0 10px', borderRadius: 16, border: 'none', gap: 5, background: 'var(--fill2)', fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}
          >
            <SquaresFour weight="bold" size={13} />
            Alle Widgets
          </button>
        </div>
        <div className="immo-scroll flex flex-col flex-1 min-h-0 overflow-auto" style={{ padding: '4px 20px 22px', gap: 14 }}>
          {children}
        </div>
      </motion.div>
    </motion.section>
  );
}

/** Abschnittskopf in Panels: Titel 18/800 + optionale Aktion rechts. */
export function PanelAbschnitt({ titel, untertitel, aktion }: { titel: string; untertitel?: string; aktion?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
      <div className="flex flex-col" style={{ gap: 2 }}>
        <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>{titel}</span>
        {untertitel && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{untertitel}</span>}
      </div>
      {aktion}
    </div>
  );
}
